import { useEffect, useRef } from 'react';
import { AccountSection } from './AccountSection';
import { AgentsSection } from './AgentsSection';
import { BillingSection } from './BillingSection';
import { ContactsSection } from './ContactsSection';
import { InvitesSection } from './InvitesSection';
import { AboutSection } from './AboutSection';
import { NotificationPrefsSection } from './NotificationPrefsSection';
import { SignOutSection } from './SignOutSection';
import { SpacesSection } from './SpacesSection';
import { ThemeSection } from './ThemeSection';
import { useSettingsDrawer } from './SettingsDrawerContext';
import type { SettingsSection } from './SettingsDrawerContext';
import { useFocusTrap } from '../hooks/useFocusTrap';

const SECTIONS: { id: SettingsSection; label: string }[] = [
  { id: 'account', label: 'Account' },
  { id: 'billing', label: 'Billing' },
  { id: 'appearance', label: 'Appearance' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'agents', label: 'Agents' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'invites', label: 'Invites' },
  { id: 'spaces', label: 'Spaces' },
  { id: 'about', label: 'About' },
  { id: 'sign-out', label: 'Sign out' },
];

export function SettingsDrawer() {
  const { isOpen, activeSection, setActiveSection, close } = useSettingsDrawer();
  const drawerRef = useRef<HTMLElement | null>(null);
  const triggerRef = useRef<Element | null>(null);

  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement;
    } else if (triggerRef.current instanceof HTMLElement) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  useFocusTrap(drawerRef, isOpen);

  if (!isOpen) return null;

  return (
    <div
      className="settings-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <aside
        ref={drawerRef}
        className="settings-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
      >
        <header className="settings-drawer-header">
          <h2>Settings</h2>
          <button
            type="button"
            className="settings-drawer-close"
            aria-label="Close settings"
            onClick={close}
          >
            ×
          </button>
        </header>
        <div className="settings-drawer-body">
          <nav className="settings-nav" aria-label="Settings sections">
            <ul>
              {/* Org Builder — the ONLY in-app entry point to the /org canvas (there was
                  none anywhere in the running app). A real navigation (hand-rolled
                  path-matching routing in App.tsx serves /org), badged "preview" to
                  mirror the DM preview badge in the sidebar. */}
              <li>
                <a className="settings-nav-button settings-nav-link" href="/org">
                  <span>Org Builder</span>
                  <span className="sidebar-header-badge" aria-label="Coming soon">
                    preview
                  </span>
                </a>
              </li>
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className={`settings-nav-button ${
                      activeSection === s.id ? 'settings-nav-button-active' : ''
                    }`}
                    onClick={() => setActiveSection(s.id)}
                    aria-current={activeSection === s.id ? 'page' : undefined}
                  >
                    {s.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
          <section className="settings-content" aria-live="polite">
            <ActiveSection section={activeSection} />
          </section>
        </div>
      </aside>
    </div>
  );
}

function ActiveSection({ section }: { section: SettingsSection }) {
  switch (section) {
    case 'account':
      return <AccountSection />;
    case 'billing':
      return <BillingSection />;
    case 'appearance':
      return <ThemeSection />;
    case 'notifications':
      return <NotificationPrefsSection />;
    case 'agents':
      return <AgentsSection />;
    case 'contacts':
      return <ContactsSection />;
    case 'invites':
      return <InvitesSection />;
    case 'spaces':
      return <SpacesSection />;
    case 'about':
      return <AboutSection />;
    case 'sign-out':
      return <SignOutSection />;
  }
}
